'use client';

export type BrowserVaultEntry<TDraft = unknown> = {
  id: string;
  toolId: string;
  toolName: string;
  toolCategory: string;
  inputLabel: string;
  outputFormat: string;
  fileName: string;
  fileSize: string;
  mimeType: string;
  createdAt: string;
  thumbnailColor: string;
  draft: TDraft | null;
};

const META_KEY = '2xora_browser_vault_meta_v1';
const DB_NAME = '2xora-browser-vault';
const STORE_NAME = 'files';
const MAX_ENTRIES = 24;

function canUseBrowserVault() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
}

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${bytes} B`;
}

function readMeta<TDraft = unknown>() {
  if (typeof window === 'undefined') {
    return [] as BrowserVaultEntry<TDraft>[];
  }

  try {
    const raw = window.localStorage.getItem(META_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as BrowserVaultEntry<TDraft>[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeMeta<TDraft = unknown>(entries: BrowserVaultEntry<TDraft>[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(META_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
}

function openVaultDatabase() {
  if (!canUseBrowserVault()) {
    return Promise.reject(new Error('Browser vault is not available.'));
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open browser vault.'));
  });
}

async function putBlob(id: string, blob: Blob) {
  const db = await openVaultDatabase();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(blob, id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Failed to store browser file.'));
  });

  db.close();
}

async function deleteBlob(id: string) {
  const db = await openVaultDatabase();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Failed to remove browser file.'));
  });

  db.close();
}

export async function getBrowserVaultBlob(id: string) {
  const db = await openVaultDatabase();

  const blob = await new Promise<Blob | null>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(id);
    request.onsuccess = () => resolve((request.result as Blob | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('Failed to read browser file.'));
  });

  db.close();
  return blob;
}

export function listBrowserVaultEntries<TDraft = unknown>() {
  return readMeta<TDraft>().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function getBrowserVaultEntry<TDraft = unknown>(id: string) {
  return readMeta<TDraft>().find((entry) => entry.id === id) ?? null;
}

export async function saveBrowserVaultEntry<TDraft>({
  toolId,
  toolName,
  toolCategory,
  inputLabel,
  outputFormat,
  fileName,
  thumbnailColor,
  blob,
  draft,
}: {
  toolId: string;
  toolName: string;
  toolCategory: string;
  inputLabel: string;
  outputFormat: string;
  fileName: string;
  thumbnailColor: string;
  blob: Blob;
  draft?: TDraft | null;
}) {
  if (!canUseBrowserVault()) {
    throw new Error('Browser vault is not available.');
  }

  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `vault-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  await putBlob(id, blob);

  const nextEntry: BrowserVaultEntry<TDraft> = {
    id,
    toolId,
    toolName,
    toolCategory,
    inputLabel,
    outputFormat,
    fileName,
    fileSize: formatFileSize(blob.size),
    mimeType: blob.type || 'application/octet-stream',
    createdAt: new Date().toISOString(),
    thumbnailColor,
    draft: draft ?? null,
  };

  const currentEntries = readMeta<TDraft>();
  const nextEntries = [nextEntry, ...currentEntries];
  const overflow = nextEntries.slice(MAX_ENTRIES);
  writeMeta(nextEntries);

  if (overflow.length) {
    await Promise.allSettled(overflow.map((entry) => deleteBlob(entry.id)));
  }

  return nextEntry;
}
